import { useState } from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppLanguage, changeAppLanguage, getAppLanguage } from '../i18n/i18n';

export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const currentLanguage = getAppLanguage(i18n.resolvedLanguage);

  const chooseLanguage = async (language: AppLanguage) => {
    setAnchorEl(null);
    await changeAppLanguage(language);
  };

  const trigger = compact ? (
    <Tooltip title={t('common.language.change')}>
      <IconButton
        size="small"
        aria-label={t('common.language.change')}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <LanguageIcon />
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      variant="text"
      size="small"
      startIcon={<LanguageIcon />}
      aria-label={t('common.language.change')}
      onClick={(event) => setAnchorEl(event.currentTarget)}
      sx={{ textTransform: 'none' }}
    >
      {t(`common.language.${currentLanguage}`)}
    </Button>
  );

  return (
    <>
      {trigger}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {(['en', 'sv'] as AppLanguage[]).map((language) => (
          <MenuItem
            key={language}
            selected={language === currentLanguage}
            onClick={() => void chooseLanguage(language)}
          >
            <ListItemIcon>
              {language === currentLanguage ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{t(`common.language.${language}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
